import { useEffect, useReducer } from "react"
import { useTranslation } from "react-i18next";
import GridMultipleSelectorInput from "../../../../components/controls/GridMultipleSelectorInput";
import { DefaultGrid } from "../../../../components/grids/default/defaultGrid";
import useGridFilter from "../../../../system/hooks/useGridFilter";
import { StoreSelectorModal } from "../../../Dictionaries/Store";
import { v4 as uuidv4 } from 'uuid'
import { initialState, reducer } from "../state/ConstructorCreate/reducer";
import GridSelectorInput from "../../../../components/controls/GridSelectorInput";
import { SchemeSelectedModal } from "./SchemeSelectedModal";
import { ContractorSelectorModal } from "../../../Dictionaries/Contractor";
import { IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common";
import { SelectedEntityContractorPluginSettings } from "../pluginSettings/SelectedEntityContractorPluginSettings";
import { ConstructorDocumentSelectedModal } from "./ConstructorDocumentSelectedModal";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { ConstructorSchemesDataProvider } from "../../../../Services/DataProviders/ConstructorSchemesDataProvider";
import { BaseModalWindow } from "../../../../components/modalWindows/BaseModalWindow";
import { ICreateRuleDTO, IRuleViewDTO } from "../../../../libs/coreapi-dto/dirs/constructorSchemes";
import { useForm } from "../../../../system/hooks/useForm";
import { ConstructorSchemesCreatePluginSettings } from "../pluginSettings/ConstructorSchemesCreatePluginSettings";
import { ConstructorSchemesStoresCreatePluginSettings } from "../pluginSettings/ConstructorSchemesStoresCreatePluginSettings";

type Create = 'Create'
type Edit = 'Edit'
interface IConstructorSchemesCreateProps<TOpen> {
    closeAndRefresh: () => void
    close: () => void
    editItem: TOpen extends Create ? null : IRuleViewDTO
    lockFromPermission?: boolean
}

interface IValidator {
    document: string
    store: string
    scheme: string
}

export const ConstructorSchemesCreate = <TOpen extends Create | Edit,>(props: IConstructorSchemesCreateProps<TOpen>) => {
    const appContext = useAppContext()
    const constructorSchemesDP = new ConstructorSchemesDataProvider(appContext.coreApiService)

    const [state, dispatch] = useReducer(reducer, initialState)
    const [contractorsGridFilter, dispatchContractorsGridFilter] = useGridFilter();
    const [storesGridFilter, dispatchStoresGridFilter] = useGridFilter();

    const { t } = useTranslation();
    const errorsT = (value: string) => t('errors.' + value);

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            document: {
                required: {
                    value: state.kizConstructorDoc ? false : true,
                    message: errorsT("required"),
                },
            },
            store: {
                required: {
                    value: state.stores.length ? false : true,
                    message: errorsT("required"),
                },
            },
            scheme: {
                required: {
                    value: state.scheme ? false : true,
                    message: errorsT("required"),
                },
            },
        },

    });

    const filterRule = () => {
        let filter = {
            numberPerPage: 1,
            pageNumber: 1,
            columnFilters: [
                {
                    name: "idGlobal",
                    value: props.editItem?.idGlobal,
                    operator: "Eq"
                }
            ],
        } as IViewBody
        return filter;
    }

    useEffect(() => {
        if (props.editItem) {
            let filter = filterRule();
            constructorSchemesDP.viewRule(filter, (data)=>{
                let rule = data[0];
                if (props.editItem)
                constructorSchemesDP.contractors(props.editItem.idGlobal, (contractors)=>{
                    dispatch({
                        type: 'onInit', payload: {
                            idDoc: rule.idKizConstructorDocument,
                            docEntity: { idGlobal: rule.idKizConstructorDocument, displayName: rule.displayName },
                            idTable: rule.idTable,
                            idStore: rule.idStoreGlobal as string,
                            storeEntity: { idGlobal: rule.idStoreGlobal as string, displayName: rule.storeName },
                            stores: [{ idGlobal: rule.idStoreGlobal as string, displayName: rule.storeName }],
                            idScheme: rule.idSchemeGlobal as string,
                            schemeEntity: {idGlobal: rule.idSchemeGlobal as string, displayName: rule.schemeName as string},
                            contractors: contractors ? contractors.map(x=>{ return {idGlobal: x.idContractorGlobal, displayName: x.contractorName} }) : []
                        }
                    })
                },()=>{})

            })
            
        }
    }, [])

    function clearDocument()
    {
        dispatch({
            type: 'onInit', payload: {
                idDoc: "",
                docEntity: null,
                idTable: null,
                idStore: "",
                storeEntity: null,
                stores: [],
                idScheme: "",
                schemeEntity: null,
                contractors: []
            }
        })
    }

    function clearScheme()
    {
        dispatch({ type: 'changeScheme', payload: { id: "", entity: null } });
    }

    function clearStore()
    {
        dispatch({ type: 'setStores', payload: { ids: [], entities: [] } })
    }

    function clearContractors()
    {
        dispatch({ type: 'setContractors', payload: { id: [], entities: [] } })
    }

    function buildRuleDTO(): ICreateRuleDTO {
        const stores = state.stores.length ? state.stores : state.store ? [state.store] : [];
        
        return {
            ...state.createRule,
            idStoresGlobal: stores.map(store => store.idGlobal),
        };
    }

    function saveRules() {
        if (!isValid()) return;

        const stores = state.stores.length ? state.stores : state.store ? [state.store] : [];
        if (!stores.length) return;

        const dto = buildRuleDTO();

        if (props.editItem) {
            constructorSchemesDP.updateRule(props.editItem.idGlobal, dto, () => props.closeAndRefresh());
        } else {
            let rulesLeftToCreate = stores.length;

            stores.forEach((store) => {
                constructorSchemesDP.createRule(
                    { ...dto, idStoresGlobal: [store.idGlobal] },
                    () => {
                        rulesLeftToCreate -= 1;
                        if (rulesLeftToCreate === 0) props.closeAndRefresh();
                    }
                );
            });
        }
    }

    return (
        <BaseModalWindow
            ok={{
                onClick: saveRules,
                disabled: props.lockFromPermission
            }}
            cancel={{ onClick: () => props.close() }}
            header= {props.editItem ? 'Редактирование' : 'Создание'} 
        >
            {
                <div>
                    <GridSelectorInput
                        required
                        id='idSchemeDocument'
                        selectorModalJsx={ConstructorDocumentSelectedModal}
                        label={'Документ'}
                        selectedEntity={state.kizConstructorDoc}
                        onSelect={(entity: IEntitySimpleDTO) => {
                            clearScheme();
                            constructorSchemesDP.viewDocById(entity.idGlobal, (e) => {
                                dispatch({ type: 'changeKizConstructorDocument', payload: { id: entity.idGlobal, idTable: e[0].idTable, entity } })
                            })
                            setErrors({ ...errors, store: undefined })
                        }}
                        onClear={()=>{ clearDocument() }}
                        onFocus={() => setErrors({ ...errors, document: undefined })}
                        error={errors.document}
                        disabled={props.lockFromPermission}
                    />
                    {
                        state.createRule.idKizConstructorDocument &&
                        <GridMultipleSelectorInput
                            id='idStoreGlobal'
                            selectorModalJsx={StoreSelectorModal}
                            label={'Склады'}
                            selectedEntities={state.stores}
                            onSelect={(entities: IEntitySimpleDTO[]) => {
                                dispatch({ type: 'setStores', payload: { ids: entities.map(x => x.idGlobal), entities } })
                                setErrors({ ...errors, scheme: undefined })
                            }}
                            onClear={()=>{ clearStore() }}
                            onFocus={() => setErrors({ ...errors, store: undefined })}
                            error={errors.store}
                            disabled={props.lockFromPermission}
                        />
                    }
                    {
                        state.stores.length > 0 &&
                        <DefaultGrid
                            localData
                            gridId={`idStoreGrid-${uuidv4()}`}
                            data={state.stores}
                            filter={storesGridFilter}
                            totalCount={state.stores.length}
                            plugin={ConstructorSchemesStoresCreatePluginSettings}
                            selectedItem={undefined}
                            onSelect={(row) => { }}
                            onSort={(i) => { }}
                            onFilterDelete={(i) => { dispatchStoresGridFilter({ type: "deleteColumnFilter", payload: i.propertyName }) }}
                            onPageNumberChange={(n) => { dispatchStoresGridFilter({ type: "changePageNumber", payload: { pageNumber: n } }) }}
                            onNumberPerPageChange={(n) => { dispatchStoresGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } }) }}
                            hiddenPagination={undefined}
                        />
                    }
                    {
                        state.stores.length > 0 && state.idTable &&
                        <GridSelectorInput
                            id={`idScheme-${uuidv4()}`}
                            label={'Схема'}
                            idTable={state.idTable}
                            selectorModalJsx={SchemeSelectedModal}
                            selectedEntity={state.scheme}
                            onSelect={(entity: IEntitySimpleDTO) => dispatch({ type: 'changeScheme', payload: { id: entity.idGlobal, entity } })}
                            onClear={()=>{ clearScheme() }}
                            onFocus={() => setErrors({ ...errors, scheme: undefined })}
                            error={errors.scheme}
                            disabled={props.lockFromPermission}
                        />
                    }
                    {
                        state.createRule.idSchemeGlobal &&
                        <GridMultipleSelectorInput
                            id={`idContractor-${uuidv4()}`}
                            selectorModalJsx={ContractorSelectorModal}
                            label={'Контрагенты'}
                            selectedEntities={state.contractors}
                            onSelect={(entity: IEntitySimpleDTO[]) => dispatch({ type: 'setContractors', payload: { id: entity.map(x => x.idGlobal), entities: entity } })}
                            onClear={()=>{ clearContractors() }}
                            disabled={props.lockFromPermission}
                        />
                    }
                    {
                        state.createRule.idSchemeGlobal &&
                        <DefaultGrid
                            localData
                            gridId={uuidv4()}
                            data={state.contractors}
                            filter={contractorsGridFilter}
                            totalCount={state.contractors.length}
                            plugin={ConstructorSchemesCreatePluginSettings}  
                            selectedItem={undefined}
                            onSelect={(row) => { }}
                            onSort={(i) => { }}
                            onFilterDelete={(i) => { dispatchContractorsGridFilter({ type: "deleteColumnFilter", payload: i.propertyName }) }}
                            onPageNumberChange={(n) => { dispatchContractorsGridFilter({ type: "changePageNumber", payload: { pageNumber: n } }) }}
                            onNumberPerPageChange={(n) => { dispatchContractorsGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } }) }}
                            hiddenPagination={undefined}
                        />
                    }
                </div>
            }
        </BaseModalWindow >
    )
}

